
/*
 * MITSU FASHION
 * FAST DYNAMIC SKU COLOUR PICKER
 *
 * Examples:
 * 5179-white-34
 * 5179-purple-34
 * 5179-black-34
 *
 * Automatic SKU grouping.
 * No manual collections or metafields.
 */

(() => {
  const PAGE_SIZE = 250;
  const CONCURRENT_REQUESTS = 4;

  const CACHE_KEY = "mitsu-product-catalog-v4";
  const CACHE_DURATION = 30 * 60 * 1000;

  let catalogPromise = null;
  const processedProducts = new Map();

  function normalize(value) {
    return String(value || "").trim().toLowerCase();
  }

  function getGroup(sku) {
    return normalize(sku).split("-")[0] || "";
  }

  function getSkuColour(sku) {
    return normalize(sku).split("-")[1] || "";
  }

  function getProductColour(product, matchingVariant) {
    const options = product.options || [];

    const colourIndex = options.findIndex(option => {
      const name = normalize(
        typeof option === "string" ? option : option.name
      );

      return name === "color" || name === "colour";
    });

    if (colourIndex !== -1) {
      const variantColour =
        matchingVariant?.[`option${colourIndex + 1}`];

      if (variantColour) return String(variantColour);
    }

    return getSkuColour(matchingVariant?.sku);
  }

  /* ---------------------------
     SESSION CACHE
  --------------------------- */

  function getCachedCatalog() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);

      if (!raw) return null;

      const cached = JSON.parse(raw);

      if (
        Date.now() - cached.timestamp >
        CACHE_DURATION
      ) {
        sessionStorage.removeItem(CACHE_KEY);
        return null;
      }

      return cached.products;
    } catch {
      return null;
    }
  }

  function saveCatalog(products) {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          timestamp: Date.now(),
          products
        })
      );
    } catch {
      // Storage can be unavailable or full.
    }
  }

  /* ---------------------------
     CREATE COLOUR SWATCH
  --------------------------- */

  function createColourLink(
    product,
    colour,
    currentId,
    root
  ) {
    const link = document.createElement("a");

    link.className = "sku-colour-link";

    if (String(product.id) === String(currentId)) {
      link.classList.add("active");
      link.setAttribute("aria-current", "page");
    }

    link.href =
      `${root}products/${encodeURIComponent(product.handle)}`;

    link.title = colour;
    link.setAttribute(
      "aria-label",
      `View ${colour} colour`
    );

    const src =
      product.images?.[0]?.src ||
      product.image?.src ||
      product.image ||
      "";

    if (typeof src === "string" && src) {
      const img = document.createElement("img");

      img.src = src;
      img.alt = colour;
      img.loading = "eager";
      img.decoding = "async";
      img.width = 56;
      img.height = 56;
      img.className = "sku-colour-link__image";

      link.appendChild(img);
    } else {
      const span = document.createElement("span");
      span.textContent = colour;
      link.appendChild(span);
    }

    return link;
  }

  /* ---------------------------
     DISPLAY MATCHING PRODUCTS
  --------------------------- */

  function renderForPicker(picker, products) {
    const model = normalize(picker.dataset.skuModel);
    const currentId = picker.dataset.productId;
    const root = picker.dataset.shopRoot || "/";

    const results = picker.querySelector(
      "[data-sku-colour-results]"
    );

    if (!model || !results) return;

    let known = processedProducts.get(picker);

    if (!known) {
      known = new Map();

      // Preserve existing current product swatch.
      const existing = results.querySelector(
        ".sku-colour-link.active"
      );

      if (existing) {
        known.set(String(currentId), existing);
      }

      processedProducts.set(picker, known);
    }

    const fragment = document.createDocumentFragment();

    products.forEach(product => {
      if (known.has(String(product.id))) return;

      const variant = (product.variants || []).find(
        item => getGroup(item.sku) === model
      );

      if (!variant) return;

      const colour = getProductColour(product, variant);

      if (!colour) return;

      const link = createColourLink(
        product,
        colour,
        currentId,
        root
      );

      known.set(String(product.id), link);
      fragment.appendChild(link);
    });

    if (fragment.childNodes.length) {
      results.appendChild(fragment);
    }
  }

  function updatePickers(products) {
    document.querySelectorAll(
      ".sku-related-picker[data-sku-model]"
    ).forEach(picker => {
      renderForPicker(picker, products);
    });
  }

  /* ---------------------------
     FETCH CATALOGUE PAGES
  --------------------------- */

  async function fetchPage(root, page) {
    const response = await fetch(
      `${root}collections/all/products.json?limit=${PAGE_SIZE}&page=${page}`,
      {
        credentials: "same-origin"
      }
    );

    if (!response.ok) {
      throw new Error(
        `Catalogue request failed: ${response.status}`
      );
    }

    const data = await response.json();

    return data.products || [];
  }

  async function loadCatalog(root) {
    const cached = getCachedCatalog();

    if (cached) {
      updatePickers(cached);
      return cached;
    }

    const allProducts = [];

    let nextPage = 1;
    let finished = false;

    async function worker() {
      while (!finished) {
        const page = nextPage++;

        const products = await fetchPage(root, page);

        allProducts.push(...products);

        // Display matches immediately,
        // without waiting for the full catalogue.
        updatePickers(products);

        if (products.length < PAGE_SIZE) {
          finished = true;
          break;
        }
      }
    }

    await Promise.all(
      Array.from(
        { length: CONCURRENT_REQUESTS },
        () => worker()
      )
    );

    saveCatalog(allProducts);

    return allProducts;
  }

  function getCatalog(root) {
    if (!catalogPromise) {
      catalogPromise = loadCatalog(root).catch(error => {
        catalogPromise = null;
        throw error;
      });
    }

    return catalogPromise;
  }

  /* ---------------------------
     INITIALISE PICKER
  --------------------------- */

  async function initPicker(picker) {
    if (picker.dataset.skuInitialized === "true") {
      return;
    }

    picker.dataset.skuInitialized = "true";

    const model = normalize(picker.dataset.skuModel);
    const root = picker.dataset.shopRoot || "/";

    if (!model) return;

    try {
      const catalog = await getCatalog(root);

      renderForPicker(picker, catalog);
    } catch (error) {
      console.warn(
        "Mitsu SKU colour picker:",
        error
      );
    }
  }

  function initAllPickers() {
    document.querySelectorAll(
      ".sku-related-picker[data-sku-model]"
    ).forEach(initPicker);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initAllPickers
    );
  } else {
    initAllPickers();
  }

  document.addEventListener(
    "shopify:section:load",
    initAllPickers
  );
})();
